import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Lock,
  MailOutline,
  Phone,
  AccountCircle,
  Check,
  Close,
} from "@mui/icons-material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import GovernmentIDSubmission from "./GovernmentIDSubmission";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import ProfileImageCropper from "./ImageCropper";
import ImageCropper from "./ImageCropper";
import { getCroppedImage } from "./CropImageHelper";
import {
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
  textFieldStyles,
} from "../../../Styles/Styles";

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Separate loading state for updating profile button
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organization, setOrganization] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(true);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedImageURL, setCroppedImageURL] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordStrengthMessage, setPasswordStrengthMessage] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const token = localStorage.getItem("token");

  const isPasswordStrong = (password) => {
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasAlphabet = /[a-zA-Z]/.test(password);
    return hasNumber && hasSpecialChar && hasAlphabet;
  };

  useEffect(() => {
    if (newPassword) {
      setPasswordStrengthMessage(
        isPasswordStrong(newPassword)
          ? "Strong password"
          : "Password should include numbers, special characters, and alphabets"
      );
    } else {
      setPasswordStrengthMessage("");
    }
  }, [newPassword]);

  useEffect(() => {
    setLoading(true);
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/profile`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user details");
        }

        const userData = await response.json();
        setUser(userData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUserProfile();
    } else {
      setError("User not authenticated.");
      setLoading(false);
    }
  }, [token]);

  // Separate useEffect to update form fields once user data is available
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setOrganization(user.organization || "");
      setPhoneNumber(user.phoneNumber || "");
      setCountryCode(user.countryCode || "");
      setShowPhoneNumber(user.showPhoneNumber ?? true);
      setEmail(user.email || ""); // Read-only
      setUsername(user.username || "");
      setProfileImage(user.image || "");
    }
  }, [user]);

  // Handle file input change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setIsCropperOpen(true);
    }
  };

  // Handle final cropped image
  const handleCroppedImage = async () => {
    if (!croppedAreaPixels) {
      console.error("Cropped area is not set");
      return;
    }

    try {
      const croppedBlob = await getCroppedImage(
        selectedImage,
        croppedAreaPixels,
        200,
        200
      );
      setCroppedImage(croppedBlob);
      setIsCropperOpen(false);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  useEffect(() => {
    if (croppedImage) {
      const objectURL = URL.createObjectURL(croppedImage);
      setCroppedImageURL(objectURL);

      return () => URL.revokeObjectURL(objectURL); // Cleanup memory
    }
  }, [croppedImage]);

  // Check username availability with debounce
  useEffect(() => {
    const checkUsernameAvailability = async () => {
      if (username && username !== user?.username) {
        setIsCheckingUsername(true);
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/check-username?username=${username}`
          );
          const data = await response.json();
          setIsUsernameAvailable(data.available);
        } catch (error) {
          console.error("Error checking username availability:", error);
        }
        setIsCheckingUsername(false);
      }
    };

    const debounceTimeout = setTimeout(() => {
      checkUsernameAvailability();
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [username, user]);

  const handleFormSubmit = async (e) => {
    setIsSubmitting(true);
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("organization", organization);
    formData.append("username", username);
    formData.append("phoneNumber", phoneNumber);
    formData.append("countryCode", countryCode);
    formData.append("showPhoneNumber", showPhoneNumber);
    if (croppedImage) {
      formData.append(
        "profileImage",
        croppedImage,
        `${firstName || "profile"}.jpg`
      );
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/update-profile`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }
      const updatedUser = await response.json();

      // Update local storage with the updated user data
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Update the state with the new user data
      setUser(updatedUser);

      setSuccess("Profile updated successfully!");
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    if (newPassword !== confirmNewPassword) {
      setError("New password and confirm password do not match");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/update-password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ currentPassword, newPassword }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update password");
      }

      setSuccess("Password updated successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setShowPasswordFields(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3, mb: 5 }}>
      <Helmet>
        <title>Profile - Listed.ky</title>
        <meta name="description" content="Profile - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      {user ? (
        <>
          <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
            Profile
          </Typography>
          <form onSubmit={handleFormSubmit} encType="multipart/form-data">
            <Grid
              container
              spacing={2}
              sx={{
                mb: 2,
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" }, // Column on small screens, row on medium+
              }}
            >
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    cursor: "pointer",
                  }}
                >
                  {/* Hidden File Input */}
                  <input
                    id="profile-image-upload"
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />

                  {/* Avatar with Hover Overlay */}
                  <label
                    htmlFor="profile-image-upload"
                    style={{ marginBottom: "0px" }}
                  >
                    <Avatar
                      src={
                        croppedImageURL ||
                        user.image ||
                        "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
                      }
                      alt={user?.firstName?.[0]}
                      sx={{ width: 56, height: 56 }}
                    />
                  </label>

                  {/* Camera Icon Overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 56,
                      height: 56,
                      backgroundColor: "rgba(0, 0, 0, 0.5)", // Dark overlay
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0, // Hidden by default
                      transition: "opacity 0.3s ease-in-out",
                      "&:hover": { opacity: 1 }, // Show on hover
                    }}
                  >
                    <label
                      htmlFor="profile-image-upload"
                      style={{ marginBottom: "0px" }}
                    >
                      <IconButton
                        component="span"
                        sx={{ color: "white", ...noOutline }}
                      >
                        <CameraAltOutlinedIcon />
                      </IconButton>
                    </label>
                  </Box>
                </Box>
              </Grid>

              {/* Government ID Submission Section */}
              {user.isVerifiedbyID !== "approved" &&
                user.isVerifiedbyID !== "pending" && (
                  <Grid item xs={12} sm="auto">
                    <GovernmentIDSubmission />
                  </Grid>
                )}

              {user.isVerifiedbyID === "pending" && (
                <Grid item xs={12} sm="auto">
                  <Typography variant="body2" sx={{ color: "gray", ...fonts }}>
                    Government ID under review
                  </Typography>
                </Grid>
              )}
            </Grid>

            <TextField
              fullWidth
              margin="normal"
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              sx={{ ...textFieldStyles }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              sx={{ ...textFieldStyles }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              value={email}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline />
                  </InputAdornment>
                ),
              }}
              sx={{ ...textFieldStyles }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl
                  fullWidth
                  margin="normal"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  <InputLabel sx={{ ...fonts }}>Country Code</InputLabel>
                  <Select
                    label="Country Code"
                    margin="normal"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    required
                    sx={{ ...fonts, borderRadius: "8px" }}
                  >
                    <MenuItem value="+1345" sx={{ ...fonts }}>
                      +1345 (Cayman Islands)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const input = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
                    if (input.length <= 7) {
                      setPhoneNumber(input);
                    }
                  }}
                  inputProps={{ maxLength: 7, pattern: "[0-9]*" }}
                  InputProps={{
                    // readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...textFieldStyles }}
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPhoneNumber}
                  onChange={(e) => setShowPhoneNumber(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "var(--primary-color)", // Filled color when checked
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ ...fonts }}>
                  Show Phone Number in Ads
                </Typography>
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Organization/Company"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              sx={{ ...textFieldStyles }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              sx={{ ...textFieldStyles }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
                endAdornment: isCheckingUsername ? (
                  <CircularProgress size={20} />
                ) : isUsernameAvailable ? (
                  <Check color="success" />
                ) : (
                  <Close color="error" />
                ),
              }}
            />
            <Button
              type="submit"
              className="main-button-style"
              sx={{ mt: 2, ...mainButtonStyles }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span>Saving...</span>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                </>
              ) : (
                <>
                  <span>Save Changes</span>
                  <ArrowForwardOutlinedIcon fontSize="small" />
                </>
              )}
            </Button>
          </form>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
          <Button
            onClick={() => setShowPasswordFields((prev) => !prev)}
            // className="main-button-style"
            sx={{ mt: 2, mb: 2, ...mainButtonStyles }}
          >
            Change Password
          </Button>

          {showPasswordFields && (
            <form onSubmit={handlePasswordChangeSubmit}>
              <TextField
                fullWidth
                margin="normal"
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                      >
                        {showCurrentPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...textFieldStyles }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                helperText={passwordStrengthMessage}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword((prev) => !prev)}
                      >
                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...textFieldStyles }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Confirm New Password"
                type={showConfirmNewPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmNewPassword((prev) => !prev)
                        }
                      >
                        {showConfirmNewPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...textFieldStyles }}
              />
              <Button
                type="submit"
                className="main-button-style"
                sx={{ mt: 2, ...mainButtonStyles }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span>Updating...</span>
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  </>
                ) : (
                  <>
                    <span>Update Password</span>
                    <ArrowForwardOutlinedIcon fontSize="small" />
                  </>
                )}
              </Button>
            </form>
          )}
        </>
      ) : (
        <LoadingAnimation />
      )}
      {/* Cropping Modal */}
      {isCropperOpen && (
        <ImageCropper
          imageSrc={selectedImage}
          setCroppedAreaPixels={setCroppedAreaPixels} // Pass setter
          onCropComplete={handleCroppedImage}
          onClose={() => setIsCropperOpen(false)}
        />
      )}
    </Box>
  );
};

export default Profile;
